    import { ref, computed } from "vue";
    import { useStore } from "vuex";
    import FormInputTextField from "../FormInputTextField/FormInputTextField";
    import FormInputTextarea from "../FormInputTextarea/FormInputTextarea";
    import FormInputDropdown from "../FormInputDropdown/FormInputDropdown";
    import FormInputEmailField from "../FormInputEmailField/FormInputEmailField";
    import FormCheckbox from "../FormCheckbox/FormCheckbox";
    import FormInputNameField from "../FormInputNameField/FormInputNameField";
    import ProfilePicture from "../ProfilePicture/ProfilePicture";
    import DropdownWithTags from "../DropdownWithTags/DropdownWithTags";
    import nameValidator from "foundation/js/inputValidators/nameValidator";
    import nonEmptyStringValidator from "foundation/js/inputValidators/nonEmptyStringValidator";
    import isBoolean from "foundation/js/inputValidators/isBoolean";
    import Editor from '@hugerte/hugerte-vue'
    import { useHugeRTE } from '../../foundation/js/loadHugeRTE'


    export default {
        name: "ForumEditProfile",
        components: {
            FormInputTextField,
            FormInputTextarea,
            FormInputNameField,
            FormInputEmailField,
            FormInputDropdown,
            FormCheckbox,
            ProfilePicture,
            DropdownWithTags,
            Editor
        },
        props: {
            content: {
                type: Object,
                required: true,
            },
            continueUrl: {
                type: String,
                required: true,
            },
            continueUrlWithData: {
                type: String,
                required: true,
            }
        },
        setup(props) {
            const store = useStore();
            const personalInfo = ref({
                ProfileName: props.content.memberName ?? "",
                relationToCancer: props.content.memberRoles === "" ? null : props.content.memberRoles,
                cancerType: props.content.memberDiagnosis ?? [""],
                cancerTypeChanged: [""],
                region: props.content.memberRegion === "" ? null : props.content.memberRegion,
                dateOfBirth: props.content.memberDateOfBirth,
                gender: props.content.memberGender ?? "",
                description: (() => {
                    const text = props.content.memberProfileText ?? ""
                    if (!text) return ''
                    // Wrap in <p> tags from the start so toPlainText always finds
                    // <p> elements, regardless of whether the editor has fired its
                    // first update:modelValue event yet.
                    return '<p>' + text.replace(/\r\n|\n/g, '</p><p>') + '</p>'
                })(),
                NotificationForNewPostInMyGroups: props.content.notifications.postInGroup ?? false,
                NotificationForDirectMessage: props.content.notifications.privateMessage ?? false,
                NotificationForCommentOnMySavedPosts: props.content.notifications.commentOnMyPost ?? false,
                NotificationForNewCommentInMyGroups: props.content.notifications.commentInMyGroups ?? false,
                NotificationFrequency: props.content.notifications.frequency,
            });
            const { editorReady } = useHugeRTE()
            const notificationfrequencyOptions = [
                { "name": "Straks", "value": "Immediately" },
                { "name": "Dagligt", "value": "Daily" },
                { "name": "Ugentligt", "value": "Weekly" },
            ]
            const genderOptions =[
                { "name": "Kvinde", "value": "Kvinde"},
                { "name": "Mand", "value": "Mand" },
                { "name": "Andet", "value": "Andet" }
            ]

            const hasProfilePicture = ref(props.content.hasProfileImage === "true")
            const activeProfilePicture = ref({
                "name": props.content.memberImgName ?? "",
                "url": props.content.memberImgSrc ?? "",
                "size": props.content.memberImgSize ?? ""
            })

            const changePasswordUrl = props.content.changePasswordUrl

            const displayError = ref(false)
            const isSubmitting = computed(()=> store.state.forum.is_submitting)

            const isFormValid = () => {
                return nameValidator(personalInfo.value.ProfileName) &&
                    isBoolean(personalInfo.value.NotificationForNewPostInMyGroups) &&
                    isBoolean(personalInfo.value.NotificationForDirectMessage) &&
                    isBoolean(personalInfo.value.NotificationForCommentOnMySavedPosts) &&
                    nonEmptyStringValidator(personalInfo.value.NotificationFrequency)
            };

            const toPlainText = (html) => {
                const doc = new DOMParser().parseFromString(html || '', 'text/html')
                // Walk <p> elements explicitly rather than using textContent.
                // textContent strips <br> and </p> without separators ("EnToTre"),
                // while string-replacing </p> also picks up the editor's own whitespace
                // between tags and creates spurious extra newlines.
                // Each <p> becomes one entry in parts; <br> inside becomes \n.
                // Empty <p> (from pressing Enter on a blank line) becomes "" which
                // joins as \n\n \u2014 preserving intentional double line breaks.
                const getNodeText = (container) => {
                    let text = ''
                    container.childNodes.forEach(node => {
                        if (node.nodeName === 'BR') {
                            text += '\n'
                        } else if (node.nodeType === Node.TEXT_NODE) {
                            text += node.textContent
                        }
                    })
                    return text
                }

                const paragraphs = doc.body.querySelectorAll('p')
                const raw = paragraphs.length > 0
                    // Normal path: editor output \u2014 content in <p> elements
                    ? Array.from(paragraphs).map(getNodeText).join('\n')
                    // Fallback: <br>-only content (should not happen after init fix,
                    // but kept as a safety net)
                    : getNodeText(doc.body)

                return raw.replace(/\u00a0/g, ' ').trim()
            }


            const saveProfile = () => {
                const isValid = isFormValid()
                displayError.value = !isValid
                if (!isValid) return

                const payload = {
                    ...personalInfo.value,
                    dateOfBirth: personalInfo.value.dateOfBirth || null,
                    description: toPlainText(personalInfo.value.description)
                }

                store.dispatch('forum/submitSaveProfile', {
                    body: payload
                })
            }


            const startDeleteUserOverlay = function (e) {
                store.commit('main/set_page_overlay_content_data', { cancerforumProfileId: props.content.id, continueUrl: props.continueUrl, continueUrlWithData: props.continueUrlWithData })
    
                store.dispatch("main/openPageOverlay", {
                    overlayContent: "DeleteCancerProfileModal",
                    returnTarget: e.currentTarget,
                });
            };

            const startUserInfoOverlay = function (e) {
                store.dispatch("main/openPageOverlay", {
                    overlayContent: "MemberOverlay",
                    returnTarget: e.currentTarget,
                });
            };

            const setCancerTypes = function(tags) {
                personalInfo.value.cancerType = tags
            }

            return {
                notificationfrequencyOptions,
                genderOptions,
                hasProfilePicture,
                activeProfilePicture,
                personalInfo,
                changePasswordUrl,
                saveProfile,
                nameValidator,
                nonEmptyStringValidator,
                startDeleteUserOverlay,
                displayError,
                isSubmitting,
                setCancerTypes,
                startUserInfoOverlay,
                editorReady,
                getLabel: (labelKey) =>
                    store.getters["labels/getLabel"]({
                        labelCollection: "ForumEditProfile",
                        labelKey,
                    }),
            };
        },
    };
